import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import { getHttpClient } from "src/services/rest-api";
import { HTTPService } from "src/services/httpservice";

interface Props {
  open: boolean;
  onClose: () => void;
  contact: any;
  onConverted?: () => void;
}

const RESTAURANT_TYPES = [
  { code: "rs", label: "Restaurant" },
  { code: "pz", label: "Pizza" },
  { code: "cf", label: "Coffee" },
  { code: "pb", label: "Pub" },
  { code: "bky", label: "Bakery" },
  { code: "cky", label: "Chicken" },
  { code: "sh", label: "Sushi" },
];

const slugify = (s: string) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

interface Credentials {
  admin_username: string;
  admin_password: string;
  stores: { username: string; password: string }[];
}

const ConvertCompanyModal: React.FC<Props> = ({ open, onClose, contact, onConverted }) => {
  const [form, setForm] = useState({
    admin_name: contact?.name || "",
    company_slug: slugify(contact?.company || contact?.name || ""),
    restaurant_type: contact?.restaurant_type || "rs",
    city: "",
    no_of_branches: contact?.locations || 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Credentials | null>(null);

  const alreadyConverted = !!contact?.converted_company_uuid || contact?.pipeline_status === "CONVERTED";

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getHttpClient(
        `${HTTPService.CONTACT_REQUESTS}/${contact.id}/convert`,
        "POST",
        form,
        null,
        null
      );
      setResult(res.data?.data || null);
      onConverted?.();
    } catch (err: any) {
      setError(err?.data?.message || "Failed to convert lead.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {result ? "Company Created 🎉" : "Convert Lead to Company"}
      </DialogTitle>
      <DialogContent>
        {result ? (
          <Stack spacing={1.5} mt={1}>
            <Alert severity="success" icon={<CheckCircleIcon />}>
              Free-trial company created — credentials have also been emailed to {contact?.email}.
            </Alert>
            {[
              ["Admin username", result.admin_username],
              ["Admin password", result.admin_password],
            ].map(([label, value]) => (
              <Box key={label as string} display="flex" justifyContent="space-between" sx={{ bgcolor: "#F8FAFC", p: 1.25, borderRadius: "8px" }}>
                <Typography fontSize="13px" color="text.secondary" fontWeight={600}>{label}</Typography>
                <Typography fontSize="13px" fontFamily="monospace" fontWeight={700}>{value}</Typography>
              </Box>
            ))}
            <Typography fontSize="12px" fontWeight={700} color="text.secondary" mt={0.5}>
              Store accounts ({result.stores?.length || 0})
            </Typography>
            {(result.stores || []).map((s) => (
              <Box key={s.username} display="flex" justifyContent="space-between" sx={{ bgcolor: "#F8FAFC", p: 1.25, borderRadius: "8px" }}>
                <Typography fontSize="13px" fontFamily="monospace" fontWeight={600}>{s.username}</Typography>
                <Typography fontSize="13px" fontFamily="monospace" fontWeight={700}>{s.password}</Typography>
              </Box>
            ))}
          </Stack>
        ) : alreadyConverted ? (
          <Alert severity="info" sx={{ mt: 1 }}>This lead has already been converted to a company.</Alert>
        ) : (
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <Typography fontSize="13px" color="text.secondary">
              Pre-filled from the lead: <strong>{contact?.company || contact?.name}</strong> · {contact?.email} · {contact?.country || "IN"}
            </Typography>
            <TextField label="Admin name" size="small" fullWidth value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)} />
            <TextField label="Company slug" size="small" fullWidth value={form.company_slug} onChange={(e) => set("company_slug", slugify(e.target.value))} helperText="Login credentials are generated automatically." />
            <Stack direction="row" spacing={1.5}>
              <TextField select label="Restaurant type" size="small" fullWidth value={form.restaurant_type} onChange={(e) => set("restaurant_type", e.target.value)}>
                {RESTAURANT_TYPES.map((r) => <MenuItem key={r.code} value={r.code}>{r.label}</MenuItem>)}
              </TextField>
              <TextField label="Branches" size="small" type="number" sx={{ width: 110 }} value={form.no_of_branches} onChange={(e) => set("no_of_branches", Number(e.target.value))} inputProps={{ min: 1 }} />
            </Stack>
            <TextField label="City (optional)" size="small" fullWidth value={form.city} onChange={(e) => set("city", e.target.value)} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none", fontWeight: 600 }}>
          {result ? "Done" : "Cancel"}
        </Button>
        {!result && !alreadyConverted && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !form.admin_name || !form.company_slug || !form.restaurant_type}
            startIcon={loading ? <CircularProgress size={14} /> : undefined}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px" }}
          >
            Create Free-Trial Company
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConvertCompanyModal;
